/**
 * BPO Contact — Brand Logo Component
 * Text-based wordmark with a coral dot accent, on deep navy.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

interface LogoProps {
  variant?: 'full' | 'icon' | 'wordmark' | 'stacked';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  darkMode?: boolean;
  animated?: boolean;
}

const sizeConfig = {
  xs: { iconSize: 18, fontSize: 13, gap: 6 },
  sm: { iconSize: 24, fontSize: 16, gap: 7 },
  md: { iconSize: 30, fontSize: 20, gap: 8 },
  lg: { iconSize: 38, fontSize: 24, gap: 10 },
  xl: { iconSize: 48, fontSize: 30, gap: 12 },
  '2xl': { iconSize: 58, fontSize: 36, gap: 14 },
};

const NAVY = '#1E5FFF';
const CORAL = '#FF5A36';

const IconMark = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="flex-shrink-0"
  >
    <rect width="40" height="40" rx="9" fill={NAVY} />
    {/* Speech bubble */}
    <path
      d="M11 14a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v7a4 4 0 0 1-4 4h-7l-5 4v-4a4 4 0 0 1-2-3.5V14Z"
      fill="#fff"
    />
    {/* Coral dot accent */}
    <circle cx="29" cy="29" r="4" fill={CORAL} />
  </svg>
);

export const Logo = React.forwardRef<HTMLDivElement, LogoProps>(function Logo(
  { variant = 'full', size = 'md', className, darkMode = false, animated = false },
  ref,
) {
  const config = sizeConfig[size];
  const textColor = darkMode ? '#FFFFFF' : NAVY;

  if (variant === 'icon') {
    return (
      <div
        ref={ref}
        className={cn(animated && 'transition-transform duration-300 hover:scale-105', className)}
      >
        <IconMark size={config.iconSize} />
      </div>
    );
  }

  if (variant === 'wordmark') {
    return (
      <div
        ref={ref}
        className={cn(animated && 'transition-transform duration-300 hover:scale-105', className)}
      >
        <span
          className="font-heading font-extrabold tracking-tight leading-none"
          style={{ fontSize: config.fontSize, color: textColor }}
        >
          BPO<span style={{ color: CORAL }}>.</span>Contact
        </span>
      </div>
    );
  }

  if (variant === 'stacked') {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center gap-1',
          animated && 'transition-transform duration-300 hover:scale-105',
          className,
        )}
      >
        <IconMark size={config.iconSize} />
        <span
          className="font-heading font-extrabold tracking-tight leading-none"
          style={{ fontSize: config.fontSize * 0.7, color: textColor }}
        >
          BPO<span style={{ color: CORAL }}>.</span>Contact
        </span>
      </div>
    );
  }

  // Full (default) — icon + wordmark
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center',
        animated && 'transition-transform duration-300 hover:scale-105',
        className,
      )}
      style={{ gap: config.gap }}
    >
      <IconMark size={config.iconSize} />
      <span
        className="font-heading font-extrabold tracking-tight leading-none"
        style={{ fontSize: config.fontSize, color: textColor }}
      >
        BPO<span style={{ color: CORAL }}>.</span>Contact
      </span>
    </div>
  );
});

export const LogoMinimal = React.forwardRef<
  HTMLSpanElement,
  { className?: string; darkMode?: boolean }
>(function LogoMinimal({ className, darkMode = false }, ref) {
  const textColor = darkMode ? '#FFFFFF' : NAVY;
  return (
    <span ref={ref} className={cn('flex items-center gap-2', className)}>
      <IconMark size={20} />
      <span className="font-heading font-bold text-sm leading-none" style={{ color: textColor }}>
        BPO<span style={{ color: CORAL }}>.</span>Contact
      </span>
    </span>
  );
});

export default Logo;
